import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next'
import { useFormContext, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { format } from 'date-fns';
import { toast } from 'components/Shared/Toast';
import { useQuery, useMutation } from 'react-query'
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import clsx from 'clsx';
import { ListGroupItem, Button, ListGroup, FormGroup, Media, Collapse } from 'reactstrap'

import bootbox from 'components/Modals/Bootbox';
import DatePicker from 'components/Shared/DatePicker'
import { TenantGroupsSelect, useGroupList } from 'components/Shared/Selects/TenantGroupsSelect'
import { useEditorState, useEditorActions } from '../../../editor-context'
import { truncate } from 'utils'
import { fetcher } from 'api/client'
import { resendSpectatorEmail } from 'api/actions/contracts'
import { useProfileAbility } from 'context/profile'
import { fetchMembers, addMember } from 'api/actions/agenda'
import { Checkbox } from './General'

export function SpectatorList() {
    const { t } = useTranslation();
    const profileAbility = useProfileAbility();
    const canAddSpectator = profileAbility.can('ADD_SPECTATOR'); // can ADD_SPECTATOR

    const { document: doc, isEditable, isContentLocked, mode } = useEditorState();
    const { updateDocument } = useEditorActions();
    const { mutate: mutateResendEmail } = useMutation((payload) => fetcher(resendSpectatorEmail(payload)), {
        onSuccess: (data) => {
            toast.success(t('contracts.listing.send_successfull'));
        }
    })

    const { register, control, handleSubmit } = useFormContext({
        defaultValues: {
            spectators: doc.spectators
        }
    })

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'spectators',
    })

    const handleAdd = () => {
        if (isEditable && canAddSpectator) {
            //do something
            append({ name: '', email: '', phone: '', isOpen: true })
            // handleUpdate()
            setTimeout(() => handleUpdate(), 500)
        } else if (!canAddSpectator) {
            toast.error(t('wordList.plans.availablePyPE'));
        }
    }

    const handleRemove = async (index) => {
        if (isEditable) {
            //do something
            const resp = await bootbox.confirm(t('editor.config.delete_espectator_warning'));
            if (!resp) return;

            remove(index)
            setTimeout(() => handleUpdate(), 500)

        }
    }

    const handleUpdate = () => {
        if (isEditable) {
            //do something
            handleSubmit((data) => {
                const { spectators = [] } = data;
                console.log({spectators})

                updateDocument({ spectators })
            })()
        }
    }

    const handleResendEmail = (spectator) => {
        mutateResendEmail({
            documentId: doc._id,
            spectatorId: spectator._id
        })

    }

    return (
        <div>
            <ListGroup>
                {
                    fields.map((spectator, i) => (
                        <SpectatorItem
                            key={spectator.id}
                            spectator={spectator}
                            register={register}
                            index={i}
                            control={control}
                            isEditable={isEditable}
                            isContentLocked={isContentLocked}
                            editorMode={mode}
                            documentStatus={doc.status}
                            onRemove={handleRemove}
                            onUpdate={handleUpdate}
                            onResend={handleResendEmail}
                        />
                    ))
                }
            </ListGroup>
            <div className="text-center">
                <Button color="light" size="sm" disabled={!canAddSpectator || !isEditable} onClick={handleAdd} title={!canAddSpectator ? t('wordList.plans.availableOnPyPE') : ''}>
                    <i className={clsx('fas fa-plus', (!canAddSpectator) && 'not-allowed')}></i>{' '}
                    {t('editor.config.add_spectator')}
                </Button>
            </div>
        </div>
    )
}

function SpectatorItem({ spectator, index, register, control, isEditable, isContentLocked, editorMode, documentStatus, onUpdate, onRemove, onResend }) {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(spectator.isOpen)

    const toggle = () => setIsOpen(open => !open);
    const profileAbility = useProfileAbility();
    const canSetContractParticipantViewExpiration = profileAbility.can('SET_CONTRACT_PARTICIPANT_VIEW_EXPIRATION'); // can SET_CONTRACT_PARTICIPANT_VIEW_EXPIRATION
    const hasValidContractStatus = !['', 'DRAFT'].includes(documentStatus);
    const prefix = `spectators[${index}]`

    const name = useWatch({
        control,
        name: `${prefix}.name`,
        defaultValue: spectator.name
    })
    const email = useWatch({
        control,
        name: `${prefix}.email`,
        defaultValue: spectator.email
    })
    const hasValidEmail = email;

    const handleRemove = () => {
        onRemove(index);
    }

    const handleResend = (e) => {
        e.preventDefault();
        e.stopPropagation()
        onResend(spectator)
    }

    return (
        <ListGroupItem className="m-0 bg-light p-0">
            <Media className="text-dark d-flex align-items-center border p-2" onClick={toggle} >
                <Media className={`mr-2 spectator${index + 1} border-right pr-2`}>
                    <img src="/images/editor/icons/spectators/icon.png" alt="icon" height="40px" />
                </Media>
                <Media body>
                    <Media>
                        {truncate(name, 30) || t('editor.config.spectator')}
                    </Media>
                    <Media>
                        {email || t('editor.config.spectator_email')}
                    </Media>
                </Media>
                <Media>
                    <i className={`pointer fas fa-${isOpen ? 'chevron-down' : 'chevron-up'}`}></i>
                </Media>
            </Media>
            <Collapse isOpen={isOpen}>
                <form className="p-2" autoComplete="nope">

                    <FormGroup>
                        <label className="text-dark">{t('editor.config.spectator_name')}*</label>
                        <input
                            type="text"
                            className="form-control input-sm"
                            placeholder={t('editor.config.spectator_name_placeholder')}
                            name={`${prefix}.name`}
                            defaultValue={spectator.name}
                            ref={register()}
                            onBlur={onUpdate}
                            autoComplete="nope"
                        />
                    </FormGroup>
                    <FormGroup>
                        <label className="text-dark">{t('editor.config.spectator_email')}*</label>
                        <input
                            type="email"
                            className="form-control input-sm"
                            placeholder={t('editor.config.spectator_email_placeholder')}
                            name={`${prefix}.email`}
                            ref={register()}
                            onBlur={onUpdate}
                            defaultValue={spectator.email}
                            autoComplete="nope"
                        />
                    </FormGroup>
                    <FormGroup>
                        <label className="text-dark">{t('editor.config.spectator_phone')}</label>
                        <input
                            type="text"
                            className="form-control input-sm"
                            placeholder={t('editor.config.spectator_phone_placeholder')}
                            name={`${prefix}.phone`}
                            defaultValue={spectator.phone}
                            ref={register()}
                            onBlur={onUpdate}
                            autoComplete="nope"
                        />
                    </FormGroup>
                    {editorMode === 'contract' && canSetContractParticipantViewExpiration &&
                        <FormGroup>
                            <label className="text-dark">{t('editor.config.link_expiration')}</label>
                            <div className="link-expiration-date">
                                <Controller
                                    control={control}
                                    name={`${prefix}.linkExpirationDate`}
                                    defaultValue={spectator.linkExpirationDate || null}
                                    render={({ onChange, onBlur, value }) => (
                                        <DatePicker
                                            locale={i18n.language}
                                            selected={value ? new Date(value) : null}
                                            onChange={(date) => {
                                                onChange(date)
                                                onUpdate()
                                            }}
                                            onBlur={onBlur}
                                            minDate={Date.now()}
                                            data-action="set-link-expiration"
                                            customInput={<input className="form-control input-sm" type="text" />}
                                            dateFormat="dd/MMM/yyyy"
                                            placeholderText={t('editor.config.expiration_date_placeholder')}
                                            fullWidth
                                        />
                                    )}
                                />
                            </div>
                        </FormGroup>
                    }
                    {hasValidContractStatus && hasValidEmail &&
                        <Button color="success" block data-action="resend-email" onClick={handleResend}>
                            <i className="fas fa-share-square"></i> {t('editor.config.resend')}
                        </Button>
                    }
                    {isEditable && !isContentLocked &&
                        <div  className="text-right">
                            <Button color="light" className="text-danger" data-action="remove" onClick={handleRemove}>
                                <i className="fas fa-times"></i> {t('editor.config.spectator_delete')}
                            </Button>
                        </div>
                    }
                </form>
            </Collapse>
        </ListGroupItem>


    )
}